import { css } from 'styled-components';

const baseRemSize = 16;
export const pxToRem = (
  value: Array<string | number> | string | number,
  baseFontSize: number = baseRemSize
) => {
  const valueArray = Array.isArray(value) ? value : [value];
  return valueArray
    .map((item) => {
      if (/^-?\d+\.?\d*?$/.test(String(item))) {
        return `${parseFloat(String(item)) / baseFontSize}rem`;
      }
      return item;
    })
    .join(' ');
};

// default breakpoints in REM
const defaultBPs: BreakpointsType = {
  small: { value: 0 },
  medium: { value: 40 },
  large: { value: 64 },
  xlarge: { value: 75 },
  xxlarge: { value: 90 },
};

export const breakpointNames = ['sm', 'md', 'lg', 'xlg', 'xxlg'] as const;

export const breakpointsMap = new Proxy(
  {
    sm: 'small',
    md: 'medium',
    lg: 'large',
    xlg: 'xlarge',
    xxlg: 'xxlarge',
  } as const,
  {
    ownKeys() {
      return breakpointNames; // guarantee the Object.keys(breakpointsMap) has fixed order
    },
  }
);

export type Breakpoint = typeof breakpointNames[number];
export type BreakpointFullName = typeof breakpointsMap[Breakpoint];
export type BreakpointType = Partial<Record<Breakpoint, number | string>>;
export type BreakpointsType = Record<BreakpointFullName, { value: string | number }>;

// supports breakpoint name with both normal / smaller value (small /sm), converts value to rem
const getBreakpointValue = (breakpoints: BreakpointsType, breakpoint: Breakpoint) =>
  // @ts-ignore
  parseInt(breakpoints[breakpointsMap[breakpoint] || breakpoint].value, 10) / baseRemSize;

const getNextBreakPointValue = (
  breakpoints: BreakpointsType = defaultBPs,
  breakpoint: Breakpoint
) => {
  const bpArray = Object.keys(breakpoints);
  return getBreakpointValue(
    breakpoints,
    // @ts-ignore
    bpArray[bpArray.indexOf(breakpointsMap[breakpoint] || breakpoint) + 1]
  );
};

const getBreakpointStyles = (
  breakpoint: Breakpoint,
  style: any,
  { dir = 'up', breakpoints = defaultBPs } = {}
) => {
  const breakpointValue = getBreakpointValue(breakpoints, breakpoint);
  if (dir === 'up') {
    return css`
      ${breakpointValue === 0
        ? style
        : css`
            @media (min-width: ${breakpointValue}em) {
              ${style};
            }
          `}
    `;
  }
  return css`
    @media (max-width: ${getNextBreakPointValue(breakpoints, breakpoint) - 0.01}em) {
      ${style};
    }
  `;
};

export const breakpoint = (
  size: Breakpoint,
  style: any,
  { dir = 'up', breakpoints }: { dir?: 'up' | 'down'; breakpoints?: BreakpointsType } = {}
) => {
  if (breakpoints) {
    return getBreakpointStyles(size, style, { dir, breakpoints });
  }
  return css`
    ${(props) => {
      return getBreakpointStyles(size, style, { dir, breakpoints: props.theme.size.breakpoint });
    }};
  `;
};

export const fontSize = css`
  font-size: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-m']['font-size'].value, 10))};
  line-height: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-m']['line-height'].value, 10))};
  ${breakpoint(
    'lg',
    css`
      font-size: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-m']['font-size'].value, 10))};
      line-height: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-m']['line-height'].value, 10))};
    `
  )}
`;

export const getFontStyle = (fontType: 'body-xs' | 'body-s' | 'body-m' | 'body-l') => css`
  font-size: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small'][fontType]['font-size'].value, 10))};
  ${breakpoint(
    'lg',
    css`
      font-size: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large'][fontType]['font-size'].value, 10))};
    `
  )}
`;

export const fontSizeLarge = css`
  font-size: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-l']['font-size'].value, 10))};
  line-height: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-l']['line-height'].value, 10))};
  ${breakpoint(
    'lg',
    css`
      font-size: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-l']['font-size'].value, 10))};
      line-height: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-l']['line-height'].value, 10))};
    `
  )}
`;

export const fontSizeSmall = css`
  font-size: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-s']['font-size'].value, 10))};
  line-height: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-s']['line-height'].value, 10))};
  ${breakpoint(
    'lg',
    css`
      font-size: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-s']['font-size'].value, 10))};
      line-height: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-s']['line-height'].value, 10))};
    `
  )}
`;

export const fontSizeXSmall = css`
  font-size: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-xs']['font-size'].value, 10))};
  line-height: ${(props) =>
    pxToRem(parseInt(props.theme.size.font['bp-small']['body-xs']['line-height'].value, 10))};
  ${breakpoint(
    'lg',
    css`
      font-size: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-xs']['font-size'].value, 10))};
      line-height: ${(props) =>
        pxToRem(parseInt(props.theme.size.font['bp-large']['body-xs']['line-height'].value, 10))};
    `
  )}
`;

export const validationStyles = (validation: string, theme: any) => {
  if (validation === 'info') {
    return css`
      border: ${pxToRem(2)} solid ${theme.input.infoColor};
    `;
  }
  if (validation === 'warning') {
    return css`
      border: ${pxToRem(2)} solid ${theme.input.warningColor};
    `;
  }
  if (validation === 'valid') {
    return css`
      border: ${pxToRem(2)} solid ${theme.input.validColor};
    `;
  }
  if (validation === 'invalid') {
    return css`
      border: ${pxToRem(2)} solid ${theme.input.invalidColor};
    `;
  }
  return '';
};

/**
 * This function map breakpoint object to style, make the generate css has correct order, from small size to large size
 * @param breakPoint BreakpointType object
 * @param cssTransformer mapping callback to generate css content
 * @returns generated css string
 */
export const mapBreakpoint = <T extends BreakpointType>(
  breakPoint: T,
  cssTransformer: (key: keyof T, value: NonNullable<T[keyof T]>, breakPointObj: T) => string
): string =>
  breakpointNames
    .filter((key) => ![null, undefined, '' as any].includes(breakPoint[key])) // exclude nil values
    .map((key: keyof T) =>
      cssTransformer(key, breakPoint[key] as NonNullable<T[keyof T]>, breakPoint)
    )
    .join('');

// add styles for input css attribute with input css value. if value is object with 'sm', 'md' , 'lg' keys, values are applied for specific breakpoints
export const styleAttribute = (
  attribute: any,
  value: BreakpointType,
  convertToRem: boolean = true
) => {
  if (typeof value === 'object') {
    return css`
      ${(props) =>
        mapBreakpoint(value, (key, attrValue) =>
          // @ts-ignore
          breakpoint(key, `${attribute}: ${convertToRem ? pxToRem(attrValue) : attrValue};`, {
            // @ts-ignore
            breakpoints: props.theme.size.breakpoint,
          }).join('')
        )}
    `;
  }
  if (convertToRem && typeof value === 'number') {
    return `${attribute}: ${pxToRem(value)};`;
  }
  return `${attribute}: ${value};`;
};

export interface TextFieldProps {
  disabled?: boolean;
  theme?: any;
  size?: 'standard' | 'small' | 'medium' | 'large';
}

export const labelStyles = css<TextFieldProps>`
  display: block;
  font-weight: 700;
  margin-bottom: ${pxToRem(4)};
  padding: 0;
  ${({ size }) =>
    size === 'small' &&
    css`
      ${fontSizeSmall}
    `}
  ${(props) =>
    props.disabled &&
    css`
      color: ${props.theme.color.base.grey['300'].value};
    `}
`;

export const assistiveTextStyles = css<TextFieldProps>`
  color: ${(props) => props.theme.color.base.grey['600'].value};
  margin: ${pxToRem(-4)} 0 ${pxToRem(4)};
  ${({ size }) =>
    css`
      ${size === 'small' ? fontSizeXSmall : fontSizeSmall}
    `}
  ${(props) =>
    props.disabled &&
    css`
      color: ${props.theme.color.base.grey['300'].value};
    `}
`;

export default pxToRem;
